import { Avatar, Chip, type ChipSize } from '@/components/ui/design-system';
import { useResolvedMediaUrl } from '@/hooks/use-resolved-media-url';

/** Pre-composed Chip + Avatar for **team members** (internal users). Always
 *  `color="highlight"`. Picks the Avatar variant (`image` / `initial` /
 *  `unknown`) from what's available, and resolves API-signed image URLs
 *  internally so call sites can pass the raw `image_url` straight from the
 *  API.
 *
 *  Use this anywhere you'd previously rendered the legacy `ContactAvatar`
 *  for a team member. For external contacts use `ContactAvatar`
 *  (`@/components/ui/contact-avatar`) which forces `color="primary"`. */
export const MemberAvatar = ({
  name,
  email,
  imageUrl,
  size = 'sm',
  className,
}: {
  name?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  size?: ChipSize;
  className?: string;
}) => {
  const resolved = useResolvedMediaUrl(imageUrl ?? undefined);
  // If no `name`, derive a display string from the email's local part —
  // passing the full `john@x.com` as a "name" still happens to produce
  // the correct "J" today, but it's semantically wrong and breaks the
  // moment lg-size pulls 2 letters from it.
  const displayName = name || email?.split('@')[0] || '';

  return (
    <Chip size={size} color="highlight" className={className}>
      {resolved ? (
        <Avatar type="image" src={resolved} name={displayName || 'Member'} />
      ) : displayName ? (
        <Avatar type="initial" name={displayName} />
      ) : (
        <Avatar type="unknown" />
      )}
    </Chip>
  );
};
